import { useState, useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { MapPin, Phone, Mail, Navigation, Loader2, Route } from "lucide-react";
import StoreLocatorMap from "../components/StoreLocatorMap";
import { StoreHoursBlock } from "../components/home/StoreHoursBlock";
import api from "../api/index";
import { ENDPOINTS } from "../api/endpoints";
import { useSearchParams } from "react-router-dom";

export default function StoreLocator() {
  const [searchParams] = useSearchParams();
  const requestedStoreId = searchParams.get("storeId");
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const scrollToTopOnSelect = useRef(false);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const res = await api.get(ENDPOINTS.STORES.LOCATOR);
        if (res.data?.isOk) {
          const fetchedStores = res.data.data || [];
          setStores(fetchedStores);

          if (requestedStoreId && fetchedStores.some((s) => s._id === requestedStoreId)) {
            setSelectedStoreId(requestedStoreId);
          } else if (fetchedStores.length > 0) {
            setSelectedStoreId((prev) => prev || fetchedStores[0]._id);
          }
        }
      } catch (err) {
        console.error("Failed to load stores", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStores();
  }, []);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Attempt current location automatically so map + distance are ready when
  // opening store details from homepage cards.
  useEffect(() => {
    handleUseMyLocation();
  }, []);

  const mapStores = stores.map((store) => ({
    ...store,
    distanceKm: userLocation
      ? haversineKm(
          userLocation.lat,
          userLocation.lng,
          store.location?.latitude,
          store.location?.longitude
        )
      : Infinity,
  }));

  const sortedStores = userLocation
    ? [...mapStores].sort((a, b) => a.distanceKm - b.distanceKm)
    : mapStores;

  const selectedStore = sortedStores.find((s) => s._id === selectedStoreId);
  const otherStores = sortedStores.filter((s) => s._id !== selectedStoreId);

  const selectStore = (storeId) => {
    if (!storeId) return;
    scrollToTopOnSelect.current = storeId !== selectedStoreId;
    setSelectedStoreId(storeId);
  };

  useEffect(() => {
    if (!scrollToTopOnSelect.current || !selectedStoreId) return;
    scrollToTopOnSelect.current = false;
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }, [selectedStoreId]);

  // Keep selection valid whenever store data/sorting changes.
  useEffect(() => {
    if (!loading && sortedStores.length > 0 && !selectedStore) {
      setSelectedStoreId(sortedStores[0]._id);
    }
  }, [loading, sortedStores, selectedStore]);

  const directionsUrl = useMemo(() => {
    if (!selectedStore?.location?.latitude || !selectedStore?.location?.longitude) {
      return null;
    }

    const dest = `${selectedStore.location.latitude},${selectedStore.location.longitude}`;
    if (userLocation?.lat && userLocation?.lng) {
      const origin = `${userLocation.lat},${userLocation.lng}`;
      return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}&travelmode=driving`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dest)}`;
  }, [selectedStore, userLocation]);

  return (
    <main className="pt-[10px] pb-16 bg-card min-h-screen">
      <Helmet>
        <title>Store Locator</title>
        <meta
          name="description"
          content="Find your nearest Fonexpress store across Kenya. View locations, hours, and contact details."
        />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-wide uppercase mb-3">
            Store Locator
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
            Find a Fonexpress near you
          </h1>
          <p className="mt-3 text-muted-foreground">
            Visit us in-store for expert advice, same-day pickup, and hands-on demos.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <button
            type="button"
            onClick={handleUseMyLocation}
            disabled={locating}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-background text-sm font-medium hover:border-primary/40 transition-colors disabled:opacity-60"
          >
            {locating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Navigation className="h-4 w-4 text-primary" />
            )}
            Use my location
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="rounded-2xl border border-border overflow-hidden bg-background h-[350px]">
            {loading ? (
              <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                Loading map...
              </div>
            ) : (
              <StoreLocatorMap
                stores={sortedStores}
                userLocation={userLocation}
                selectedStoreId={selectedStoreId}
                onSelectStore={(store) => selectStore(store._id)}
              />
            )}
          </div>

          <div className="space-y-4 pr-1">
            {loading ? (
              [...Array(4)].map((_, i) => (
                <div key={i} className="h-24 rounded-xl bg-muted/60 animate-pulse" />
              ))
            ) : sortedStores.length === 0 ? (
              <p className="text-muted-foreground text-center py-10">No stores found.</p>
            ) : (
              <>
                {selectedStore && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">
                      Selected store
                    </p>
                    <button
                      type="button"
                      onClick={() => selectStore(selectedStore._id)}
                      className="w-full text-left rounded-xl border border-primary/60 bg-primary/10 p-4"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-foreground">{selectedStore.storeName}</h3>
                        {userLocation && selectedStore.distanceKm < Infinity && (
                          <span className="text-xs text-primary font-medium shrink-0">
                            {selectedStore.distanceKm.toFixed(1)} km
                          </span>
                        )}
                      </div>
                      {selectedStore.address && (
                        <p className="mt-1 text-sm text-muted-foreground flex items-start gap-1.5">
                          <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          {selectedStore.address}
                          {selectedStore.cityId?.cityName ? `, ${selectedStore.cityId.cityName}` : ""}
                        </p>
                      )}
                      {selectedStore.phone && (
                        <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5 shrink-0" />
                          {selectedStore.phone}
                        </p>
                      )}
                      {selectedStore.email && (
                        <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5 shrink-0" />
                          {selectedStore.email}
                        </p>
                      )}
                      {selectedStore.hoursDisplay && (
                        <div className="mt-2">
                          <StoreHoursBlock hoursDisplay={selectedStore.hoursDisplay} />
                        </div>
                      )}
                      {directionsUrl && (
                        <div className="mt-3">
                          <a
                            href={directionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition"
                          >
                            <Route className="h-3.5 w-3.5" />
                            Get directions from my location
                          </a>
                        </div>
                      )}
                    </button>
                  </div>
                )}

                {otherStores.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                      Other stores
                    </p>
                    <div className="space-y-3">
                      {otherStores.map((store) => (
                        <button
                          key={store._id}
                          type="button"
                          onClick={() => selectStore(store._id)}
                          className="w-full text-left rounded-xl border border-border bg-background p-4 transition-colors hover:border-primary/30"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-foreground">{store.storeName}</h3>
                            {userLocation && store.distanceKm < Infinity && (
                              <span className="text-xs text-primary font-medium shrink-0">
                                {store.distanceKm.toFixed(1)} km
                              </span>
                            )}
                          </div>
                          {store.address && (
                            <p className="mt-1 text-sm text-muted-foreground flex items-start gap-1.5">
                              <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              {store.address}
                              {store.cityId?.cityName ? `, ${store.cityId.cityName}` : ""}
                            </p>
                          )}
                          {store.phone && (
                            <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1.5">
                              <Phone className="h-3.5 w-3.5 shrink-0" />
                              {store.phone}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

function haversineKm(lat1, lng1, lat2, lng2) {
  if (lat2 == null || lng2 == null) return Infinity;
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
